import { useAppSelector } from '../redux/hooks';
import { selectedStudentSkillStateAfterTakeAssessment } from '../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { StudentSkillStates } from '../models/StudentSkillState';
import { mapSkillStatus } from '../businessLogicService/mapSkillStatus';
import { SkillSubStates } from '../models/SkillState';
import { EXIT_SLIP_DEMONSTRATED_LABEL } from '../businessLogicService/exitSlipSkillPage';

const baseClassName = 'tw-rounded-xl tw-inline-flex !tw-text-sm tw-justify-start tw-items-center tw-gap-2.5 ';

// Shared style definitions. Exported because the skill page has to dress a badge this hook cannot
// speak for: a skill demonstrated on exit tickets before the backend has recorded the substate still
// reads as NOT_TESTED here, and the page says "Demonstrated by Exit tickets" anyway — in this exact
// pill (see SkillResourceHeader and SkillChallengeDemonstrated).
export const SKILL_SUBSTATE_STYLES = {
  PROVEN_DIAGNOSTIC:
    baseClassName +
    '!tw-bg-stone-50 !tw-text-emerald-700 tw-leading-tight tw-px-3 tw-py-1 tw-inline-flex tw-items-center tw-gap-0.5 !tw-text-sm !tw-font-bold !tw-font-atkinson',
  DONE_BY_EXIT_TICKETS:
    baseClassName +
    '!tw-bg-stone-50 !tw-text-indigo-500 tw-leading-tight tw-px-3 tw-py-1 tw-inline-flex tw-items-center tw-gap-0.5 !tw-text-sm !tw-font-bold !tw-font-atkinson',
  KEEP_LEARNING: 'nc_keep-learning',
  REVISIT_THIS: baseClassName + '!tw-px-2.5 !tw-py-0.5 !tw-text-blue-500 !tw-font-bold !tw-font-atkinson !tw-leading-5',
} as const;

// Group substates by their behavior
const DISCREPANCY_SUBSTATES = [
  SkillSubStates.DISCREPANCY_DIAGNOSTIC_ASSESSMENT,
  SkillSubStates.DISCREPANCY_ASSESSMENT_ASSESSMENT,
  SkillSubStates.DISCREPANCY_DIAGNOSTIC_DIAGNOSTIC,
  SkillSubStates.DISCREPANCY_EXITSLIP_ASSESSMENT,
  SkillSubStates.DISCREPANCY_EXITSLIP_DIAGNOSTIC,
];

// Helper function to get custom styling for skill substates
const getCustomSkillConfig = (
  skillSubstateId: string,
): {
  label: string;
  className: string | null;
  showIcon?: boolean;
  iconType?: 'check-circle' | 'revisit' | undefined;
} | null => {
  switch (skillSubstateId) {
    case SkillSubStates.INFERRED_VIA_DIAGNOSTIC:
      return {
        label: 'Proven on Diagnostic',
        className: SKILL_SUBSTATE_STYLES.PROVEN_DIAGNOSTIC,
        showIcon: true,
        iconType: 'check-circle',
      };
    case SkillSubStates.INFERRED_VIA_ASSESSMENT:
      return {
        label: 'Proven with another skill',
        className: SKILL_SUBSTATE_STYLES.PROVEN_DIAGNOSTIC, // Same styling as diagnostic
        showIcon: true,
        iconType: 'check-circle',
      };
    case SkillSubStates.INFERRED_VIA_EXITSLIP:
      return {
        // Named the way the skill page's own screens name it; see EXIT_SLIP_DEMONSTRATED_LABEL.
        label: EXIT_SLIP_DEMONSTRATED_LABEL,
        className: SKILL_SUBSTATE_STYLES.DONE_BY_EXIT_TICKETS,
        showIcon: true,
        iconType: 'check-circle',
      };
    case SkillSubStates.PASSED_VIA_EXITSLIP:
      return {
        label: EXIT_SLIP_DEMONSTRATED_LABEL,
        className: SKILL_SUBSTATE_STYLES.DONE_BY_EXIT_TICKETS,
        showIcon: true,
        iconType: 'check-circle',
      };
    case SkillSubStates.NOT_TESTED:
    case SkillSubStates.NOT_INFERRED_VIA_DIAGNOSTIC:
      return {
        label: 'Not Started',
        className: 'tw-invisible', // Keep original styling
      };
    case SkillSubStates.NOT_PASSED_VIA_ASSESSMENT:
      return {
        label: 'Keep Learning',
        className: SKILL_SUBSTATE_STYLES.KEEP_LEARNING,
      };
    default:
      // Check if it's any of the discrepancy substates
      if (DISCREPANCY_SUBSTATES.includes(skillSubstateId as SkillSubStates)) {
        return {
          label: 'Revisit this',
          className: SKILL_SUBSTATE_STYLES.REVISIT_THIS,
          showIcon: true,
          iconType: 'revisit',
        };
      }
      return null;
  }
};

export interface CurrentSkillResult {
  score: number;
  source: 'nextState' | 'previousState' | 'graphql' | 'fallback';
  isComplete: boolean;
  maxScore: number;
  skillState?: StudentSkillStates;
  status?: string;
  statusClassName?: string;
  hasScore: boolean;
  showIcon?: boolean;
  iconType?: 'check-circle' | 'revisit';
}

export const FALLBACK_SKILL_RESULT: CurrentSkillResult = {
  score: 0,
  source: 'fallback',
  isComplete: false,
  maxScore: 10,
  status: 'Not Started',
  statusClassName: '!tw-invisible',
  hasScore: false,
};

/**
 * Custom hook to get the current skill result from Redux slice with fallback options
 * @param fallbackSkillState - Optional fallback skill state from GraphQL
 * @returns CurrentSkillResult object with score and metadata
 */
export const useCurrentSkillResult = (fallbackSkillState?: StudentSkillStates | null): CurrentSkillResult => {
  const reduxState = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);

  // Helper function to get status info from skill state
  const getStatusInfo = (skillState: StudentSkillStates) => {
    const { status, className } = mapSkillStatus(
      skillState.skillSubstate?.id || '',
      skillState.skillAssessment?.score || null,
    );

    // Apply special status handling and custom styling for specific skill substates
    let finalStatus = status;
    let finalClassName = className;
    let showIcon = false;
    let iconType: 'check-circle' | 'revisit' | undefined;

    // Check if we have custom configuration for this substate
    const customConfig = getCustomSkillConfig(skillState.skillSubstate?.id || '');
    if (customConfig) {
      finalStatus = customConfig.label;
      // Use custom className if provided, otherwise keep the original
      if (customConfig.className) {
        finalClassName = customConfig.className;
      }
      showIcon = customConfig.showIcon || false;
      iconType = customConfig.iconType;
    }

    return {
      status: finalStatus,
      className: finalClassName,
      showIcon,
      iconType,
    };
  };

  // We select a "primaryState" that represents the current skill status
  let primaryState: StudentSkillStates | null = null;
  let source: CurrentSkillResult['source'] = 'fallback';

  // Case A:
  // We are on a specific skill page (fallbackSkillState exists) AND
  // the Redux slice is anchored to this exact skillState → use nextState as override.
  if (
    fallbackSkillState &&
    reduxState?.previousState?.id === fallbackSkillState.id &&
    reduxState.previousState?.skillSubstate?.id === fallbackSkillState.skillSubstate?.id &&
    reduxState.previousState?.skillAssessment?.id === fallbackSkillState.skillAssessment?.id &&
    reduxState.nextState
  ) {
    primaryState = reduxState.nextState;
    source = 'nextState';
  }
  // Case B:
  // Legacy consumers that do NOT pass fallbackSkillState:
  // preserve original behavior (use the last global nextState/previousState).
  else if (!fallbackSkillState && reduxState?.nextState) {
    primaryState = reduxState.nextState;
    source = 'nextState';
  } else if (!fallbackSkillState && reduxState?.previousState) {
    primaryState = reduxState.previousState;
    source = 'previousState';
  }
  // Case C:
  // No Redux override applies → fall back to the GraphQL state for this skill.
  else if (fallbackSkillState) {
    primaryState = fallbackSkillState;
    source = 'graphql';
  }

  // Priority 3: Use provided fallback skill state
  if (primaryState) {
    const hasScore = primaryState.skillAssessment?.score !== null && primaryState.skillAssessment?.score !== undefined;

    const statusInfo = getStatusInfo(primaryState);

    return {
      score: primaryState.skillAssessment?.score || 0,
      source,
      isComplete: primaryState.skillAssessment?.isComplete || false,
      maxScore: primaryState.skillAssessment?.maxScore || 10,
      skillState: primaryState,
      status: statusInfo.status,
      statusClassName: statusInfo.className,
      hasScore,
      showIcon: statusInfo.showIcon,
      iconType: statusInfo.iconType,
    };
  }

  // Priority 4: Final fallback
  return FALLBACK_SKILL_RESULT;
};

/**
 * Helper function to get custom skill status and styling for support skills
 * @param skillSubstateId - The skill substate ID
 * @param score - The skill assessment score
 * @returns Object with status, className, and icon info for the skill
 */
export const getCustomSkillStatus = (skillSubstateId: string, score: number | null = null) => {
  // Get the base status from mapSkillStatus
  const { status, className } = mapSkillStatus(skillSubstateId, score);

  // Apply custom overrides for specific substates
  let finalStatus = status;
  let finalClassName = className;
  let showIcon = false;
  let iconType: 'check-circle' | 'revisit' | undefined;

  // Check if we have custom configuration for this substate
  const customConfig = getCustomSkillConfig(skillSubstateId);
  if (customConfig) {
    finalStatus = customConfig.label;
    // Use custom className if provided, otherwise keep the original
    if (customConfig.className) {
      finalClassName = customConfig.className;
    }
    showIcon = customConfig.showIcon || false;
    iconType = customConfig.iconType;
  }

  return {
    status: finalStatus,
    className: finalClassName,
    showIcon,
    iconType,
  };
};

/**
 * Helper function to determine if the Current Skill Status section should be shown
 * @param currentSkillResult - The current skill result from useCurrentSkillResult
 * @param currentStatus - Optional fallback status object
 * @returns boolean - true if the status section should be displayed
 */
export const shouldShowCurrentSkillStatus = (
  currentSkillResult: CurrentSkillResult | null,
  currentStatus?: { classname: string; status: string | undefined } | null,
): boolean => {
  // Don't show if status is "Not Started"
  if (currentSkillResult?.status === 'Not Started' || currentStatus?.status === 'Not Started') {
    return false;
  }

  // Don't show if skillSubstate is NOT_TESTED (using both formats to be safe)
  if (
    currentSkillResult?.skillState?.skillSubstate?.id === SkillSubStates.NOT_TESTED ||
    currentSkillResult?.skillState?.skillSubstate?.id === '1'
  ) {
    return false;
  }

  return true;
};
